/**
 * Custom field failures reported by the Location API, turned into findings.
 *
 * These are deliberately not checked in the builder. The API owns what a valid
 * custom field is, and a second copy of those rules here would drift from it —
 * so the save calls the API as it always has, and whatever comes back is shown
 * against the field that caused it.
 *
 * Three response shapes are known, and they do not agree with each other:
 *
 *   422  { message: ["name should not be empty"], error: "Unprocessable Entity" }
 *   400  { message: "A custom field already uses this name...", meta: { existingId } }
 *   400  { message: "acceptedFormat invalid." }
 *
 * `message` is an array of class-validator strings for a 422 and a single
 * sentence for a business rule, so both are normalised to a list here.
 */
import type { RuleResult } from "./types";

export interface CustomFieldFailure {
  /** Position in the array that was mapped, so the finding can address it. */
  elementIndex: number;
  slideIndex?: number;
  elementKey?: string;
  /** The field's label, for a finding that has to name it. */
  label: string;
  messages: string[];
}

interface ApiErrorBody {
  message?: string | string[];
  statusCode?: number;
}

/** Normalises both `message` shapes to a list, dropping blanks. */
export const apiMessages = (error: unknown): string[] => {
  const body = (error as { response?: { data?: ApiErrorBody } })?.response
    ?.data;
  const raw = body?.message ?? (error as { message?: string })?.message;
  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
    .filter(Boolean);
};

const LABEL_REQUIRED = /name should not be empty/i;
const NAME_TAKEN = /already uses this name/i;

/**
 * Picks the rule a failure belongs to.
 *
 * The two we recognise get copy of our own, because the API phrases them for a
 * developer — "name should not be empty" is about a payload field the user has
 * never heard of. Anything else carries the server's own sentence, which is at
 * least accurate, rather than a guess at what it meant.
 */
const ruleIdFor = (messages: string[]): string => {
  if (messages.some((message) => LABEL_REQUIRED.test(message))) {
    return "custom_field_label_required";
  }
  if (messages.some((message) => NAME_TAKEN.test(message))) {
    return "custom_field_name_taken";
  }
  return "custom_field_save_failed";
};

export const toRuleResults = (
  failures: CustomFieldFailure[]
): RuleResult[] =>
  failures.map((failure) => {
    const ruleId = ruleIdFor(failure.messages);
    return {
      ruleId,
      severity: "error" as const,
      section: "fields" as const,
      // One finding per field, so two fields failing the same way both show.
      key: `${ruleId}:${failure.slideIndex ?? ""}:${failure.elementIndex}`,
      params: {
        label: failure.label,
        message: failure.messages.join(" "),
      },
      target: {
        elementIndex: failure.elementIndex,
        slideIndex: failure.slideIndex,
        elementKey: failure.elementKey,
      },
    };
  });

/** A label without its markup, matching how the failure recorded it. */
const plainLabel = (value: unknown): string =>
  typeof value === "string"
    ? value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    : "";

/**
 * Whether the user has plausibly dealt with a failure the API reported.
 *
 * Only the API can say for certain, and it is asked again on the next save.
 * This is the weaker question the builder can answer on its own: has the thing
 * the server objected to actually changed? Answering it lets a finding strike
 * through as the user types, instead of sitting there looking unfixed until
 * they save and find out.
 *
 * A failure whose cause we cannot name stays put — better a finding that
 * lingers than one that clears while the problem remains.
 */
export const isAddressed = (
  result: RuleResult,
  element: { label?: unknown } | undefined
): boolean => {
  if (!element) return false;
  const label = plainLabel(element.label);

  if (result.ruleId === "custom_field_label_required") {
    return Boolean(label);
  }
  if (result.ruleId === "custom_field_name_taken") {
    // The collision was with this exact name, so any other name may clear it.
    const rejected = plainLabel(result.params?.label);
    return Boolean(label) && label !== rejected;
  }
  return false;
};
